"use client";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import breakpoints from "../design-tokens/09.breakpoints/breakpoints";
import debounce from "lodash-es/debounce";

// Types
export type BreakpointKeys = keyof typeof breakpoints;

export type BreakpointState = {
    base: boolean;
    sm: boolean;
    md: boolean;
    lg: boolean;
    xl: boolean;
    "2xl": boolean;
    "3xl": boolean;
    "4xl": boolean;
    "5xl": boolean;
    custom: boolean[];
    isResizing: boolean;
};

// MediaQuery Manager for UI Package
class UIMediaQueryManager {
    private static instance: UIMediaQueryManager;
    private mediaQueries: MediaQueryList[] = [];
    private callbacks: Set<() => void> = new Set();
    private readonly debouncedUpdate: () => void;
    private isInitialized = false;

    private constructor() {
        this.debouncedUpdate = debounce(() => {
            this.callbacks.forEach((callback) => callback());
        }, 100);
    }

    static getInstance() {
        if (!UIMediaQueryManager.instance) {
            UIMediaQueryManager.instance = new UIMediaQueryManager();
        }
        return UIMediaQueryManager.instance;
    }

    private initializeQueries() {
        if (typeof window === "undefined" || this.isInitialized) return;

        const queries = Object.values(breakpoints).map((bp) => `(min-width: ${bp})`);
        this.mediaQueries = queries.map((query) => {
            const mq = window.matchMedia(query);
            mq.addEventListener("change", this.debouncedUpdate);
            return mq;
        });

        this.isInitialized = true;
    }

    subscribe(callback: () => void) {
        if (!this.isInitialized) {
            this.initializeQueries();
        }
        this.callbacks.add(callback);
        return () => this.callbacks.delete(callback);
    }

    getCurrentState(): Omit<BreakpointState, "isResizing" | "custom"> {
        if (!this.isInitialized) {
            this.initializeQueries();
        }

        const keys = Object.keys(breakpoints) as BreakpointKeys[];
        const result = {} as Record<BreakpointKeys, boolean>;

        keys.forEach((key, index) => {
            result[key] = this.mediaQueries[index]?.matches || false;
        });

        return result;
    }

    cleanup() {
        this.mediaQueries.forEach((mq) => {
            mq.removeEventListener("change", this.debouncedUpdate);
        });
        this.callbacks.clear();
        this.isInitialized = false;
    }
}

// Store Interface
interface BreakpointStore {
    breakpoints: BreakpointState;
    setBreakpoints: (breakpoints: Omit<BreakpointState, "isResizing">) => void;
    setResizing: (isResizing: boolean) => void;
    initialize: () => void;
    cleanup: () => void;
}

// Create the store
export const useBreakpointStore = create<BreakpointStore>()(
    subscribeWithSelector((set, get) => {
        const manager = UIMediaQueryManager.getInstance();

        return {
            breakpoints: {
                base: true, // Mobile-first approach
                sm: false,
                md: false,
                lg: false,
                xl: false,
                "2xl": false,
                "3xl": false,
                "4xl": false,
                "5xl": false,
                custom: [],
                isResizing: false,
            } satisfies BreakpointState,

            setBreakpoints: (newBreakpoints) =>
                set((state) => ({
                    breakpoints: { ...newBreakpoints, isResizing: state.breakpoints.isResizing },
                })),

            setResizing: (isResizing) =>
                set((state) => ({
                    breakpoints: { ...state.breakpoints, isResizing },
                })),

            initialize: () => {
                if (typeof window === "undefined") return;

                // Set initial state
                const initialState = manager.getCurrentState();
                get().setBreakpoints({ ...initialState, custom: [] });

                // Subscribe to changes
                manager.subscribe(() => {
                    const newState = manager.getCurrentState();
                    get().setResizing(true);
                    get().setBreakpoints({ ...newState, custom: [] });

                    // Reset resizing flag after debounce
                    setTimeout(() => {
                        get().setResizing(false);
                    }, 150);
                });
            },

            cleanup: () => {
                manager.cleanup();
            },
        };
    })
);

// Auto-initialize the store when first accessed
if (typeof window !== "undefined") {
    useBreakpointStore.getState().initialize();
}
